import { memo, type ExpandedStateList } from '@tanstack/react-table';
import { assignInlineVars } from '@vanilla-extract/dynamic';
import { isEqual, isMatch } from 'lodash-es';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { isRowDisabled } from './sub-rows-utils.js';
import { subRowIconSize } from './SubRows.sty.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { useDataTableV2 } from '../../contexts/DataTableV2Context.js';
import { makeAccessorFunction } from '../../hooks/makeAccessorFunction.js';
import type {
  ColumnDef,
  OnChangeFn,
  Row,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableV2RowData } from '../../public.api.js';

/** @internal */
export type ExpandedState = true | Record<string, boolean>;

/**
 * @public
 */
export interface DataTableV2SubRowsBaseProps<TData extends DataTableV2RowData> {
  /**
   * @defaultValue false
   */
  subRows?:
    | boolean
    | {
        accessor?: // With a keyof TData we would have type safety for the objects first level and would be able to suggest and ensure that the key given actually exists in the given TData row data. To support pathed accessors like 'some.nested' we need to fall back to a string.
        string | ((row: TData) => TData[] | undefined);
        /**
         * Allows to specify a columnId that the subRow indicator will be injected to.
         * By default, this will be the first visible column provided by the columns prop.
         */
        subRowColumnId?: string;
        /**
         * Allows to specify a function that will evalute if a specific subRows trigger in a row
         * should be disabled.
         */
        disableSubRow?: (row: TData) => boolean;
      };
  /**
   * Callback triggered when open subrows change.
   */
  onOpenSubRowsChange?: (openSubRows: Record<string, boolean>) => void;
}

/**
 * @public
 */
export interface DataTableV2SubRowsControlledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2SubRowsBaseProps<TData> {
  /**
   * Currently open sub rows in a controlled scenario.
   */
  openSubRows?: true | Record<string, boolean>;
  /**
   * Initially open sub rows in an uncontrolled scenario.
   */
  defaultOpenSubRows?: never;
}

/**
 * @public
 */
export interface DataTableV2SubRowsUncontrolledProps<
  TData extends DataTableV2RowData,
> extends DataTableV2SubRowsBaseProps<TData> {
  /**
   * Currently open sub rows in a controlled scenario.
   */
  openSubRows?: never;
  /**
   * Initially open sub rows in an uncontrolled scenario.
   */
  defaultOpenSubRows?: true | Record<string, boolean>;
}

/**
 * DataTableV2 props for SubRows definition.
 * @public
 */
export type DataTableV2SubRowsProps<TData extends DataTableV2RowData> =
  | DataTableV2SubRowsControlledProps<TData>
  | DataTableV2SubRowsUncontrolledProps<TData>;

/**
 * Default accessor for the subRows
 * @internal
 */
export function defaultSubRowsAccessor<TData>(originalRow: TData): TData[] {
  return (originalRow as TData & { subRows: TData[] })?.subRows;
}

/**
 * Extension interface to extend the types from the tanstack table instance.
 * @internal
 */
export interface DataTableV2SubRowsInstance<TData> {
  /**
   * Returns the column id that should show the subrow expand indicator.
   * This does not consider builtin columns and only respects consumer provided columns.
   */
  getSubRowIndicatorColumnId: () => string | null;

  /**
   * Calculates the width of the maximum expanded subrow in pixels.
   * This is helpful for layout purposes to already size the column with the subrow.
   */
  getSubRowIndentationWidth: () => number;

  /**
   * Gets the current maximum open depth of the subrows.
   */
  getSubRowOpenDepth: () => number;

  /**
   * Returns whether some of the row's ancestors are disabled.
   */
  getIsAncestorDisabled: () => (row: Row<TData> | undefined) => boolean;

  /**
   * Returns true, if the table has at least one row with at least one sub-row, false otherwise.
   */
  getHasSubRows: () => boolean;

  _getRowLineConfig: () => (
    rowId: string,
  ) => Array<'Line' | 'TShape' | 'LShape' | 'Empty'>;
}

/**
 * Extension interface to extend the types from the tanstack table options.
 * @internal
 */
export interface DataTableV2SubRowsOptions<TData extends DataTableV2RowData> {
  /**
   * Defines the columnId where the subrow indicator will be injected to.
   */
  subRowColumnId?: string;

  /**
   * Enable/disable expanding for all rows.
   */
  enableExpanding?: boolean;

  /**
   * Control function that defines whether a subrow expand is disabled or not.
   */
  getRowExpandDisabled?: (row: TData) => boolean;

  /**
   * This function is called when the `expanded` table state changes.
   */
  onExpandedChange?: OnChangeFn<ExpandedState>;
}

/**
 * Extension interface to extend the types from the tanstack table instance.
 * @internal
 */
export interface DataTableV2SubRowsRow<TData extends DataTableV2RowData> {
  _getIsLastInExpandedOnLevel: () => boolean;

  /** The last expanded sub-row of this row, depending on the order of the rows. Can be a direct sub-row or a deeper nested one. */
  _getLastExpandedSubRow: () => Row<TData> | undefined;

  /** Determines whether the given subRow expander is disabled. */
  getCanExpandDisabled: () => boolean;

  /** The nesting level of the (sub) row. Top level parent row will have depth: 0, it's child depth: 1 and so on. */
  getSubRowDepth: () => number;

  /** Returns the expanded leaf rows for the row, not including any parent rows. */
  getExpandedLeafRows: () => Row<TData>[];
}

/**
 * @internal
 */
export function calculateMaxVisibleDepth(
  rowIds: string[],
  separator: string = '.',
): number {
  let maxDepth = 0;
  let prevLevel = 0;
  let prevId = rowIds.at(0) ?? '';

  const sortedRowIds = rowIds.sort();

  for (const rowId of sortedRowIds) {
    const currentLevel = rowId.split(separator).length;
    const sameBranch = rowId.startsWith(prevId);

    // Detected a gap in the expands
    if (sameBranch && currentLevel > prevLevel + 1) {
      continue;
    }

    // When we switch the branch, we need to reset the level in order to detect
    // gaps from the start.
    if (sameBranch === false) {
      prevLevel = 0;
    }

    // Now detect if we have skipped a level.
    if (currentLevel > prevLevel + 1) {
      continue;
    }

    prevId = rowId;
    prevLevel = currentLevel;
    maxDepth = Math.max(maxDepth, currentLevel);
  }

  return maxDepth;
}

/**
 * @internal
 */
export const DataTableV2SubRows: TableFeature = {
  createTable<TData extends DataTableV2RowData>(table: Table<TData>): void {
    const disabledSubRowsCache = new Map<string, boolean>();
    const rowLineConfig = new Map<
      string,
      Array<'Line' | 'TShape' | 'LShape' | 'Empty'>
    >();

    table.getIsAllRowsExpanded = () => {
      const expanded = table.getState().expanded;
      // If expanded is true, save some cycles and return true.
      if (typeof expanded === 'boolean') {
        return expanded === true;
      }

      // If expanded is an object, but has no values, return false immediately
      if (!Object.keys(expanded).length) {
        return false;
      }

      // If any row that can be expanded is not expanded, return false
      if (
        table
          .getRowModel()
          // The original code from @tanstack/react-table does not check if a row is even
          // expandable before it counts it as isExpanded.
          .flatRows.some((row) => {
            return (
              row.getCanExpand() &&
              !row.getIsExpanded() &&
              !row.getCanExpandDisabled() &&
              !table.getIsAncestorDisabled()(row)
            );
          })
      ) {
        return false;
      }

      // They must all be expanded :shrug:
      return true;
    };

    /**
     * We have to overwrite the function from tanstack expanding all sub-rows should
     * call the `onOpenSubRowsChange` with all sub-rows and not only `true`.
     */
    table.toggleAllRowsExpanded = () => {
      if (!table.getIsAllRowsExpanded()) {
        const newExpandedState: Record<string, boolean> = table
          .getExpandedRowModel()
          .flatRows.reduce((expanded, row) => {
            if (
              row.getCanExpand() === true &&
              // as `getCanExpandDisabled` does not exist on the tanstack implementation, we need to cast to our own row type
              !(row as unknown as Row<unknown>).getCanExpandDisabled()
            ) {
              expanded[row.id] = true;
            }
            return expanded;
          }, {} as Record<string, boolean>);
        table.setExpanded(newExpandedState);
      } else {
        table.setExpanded({});
      }
    };

    /**
     * Returns the current row that should show the subrow expand indicator.
     */
    table.getSubRowIndicatorColumnId = memo(
      () => [
        table.options.enableExpanding,
        table.getVisibleLeafColumns(),
        table.options.subRowColumnId,
      ],
      (enableSubRows, visibleLeafColumns, subrowColumnId) => {
        if (enableSubRows !== true) {
          return null;
        }

        // If a subrowColumnId is defined in the options, we can immediately
        // use the configured id and do not need to look for the first visible one.
        if (subrowColumnId) {
          return subrowColumnId;
        }

        for (const column of visibleLeafColumns) {
          const columnDef = column.columnDef as ColumnDef<TData>;
          if (columnDef.meta && columnDef.meta.builtin !== true) {
            return column.id;
          }
        }
        return null;
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getSubRowIndicatorColumnId',
      ),
    );

    /**
     * Gets the current maximum open depth of the sub-rows.
     */
    table.getSubRowOpenDepth = () => {
      const expandedState = table.getState().expanded;
      if (
        expandedState === true ||
        (typeof expandedState === 'object' &&
          Object.keys(expandedState).length === 0)
      ) {
        return table.getExpandedDepth();
      }

      const rowIds = Object.keys(table.getState().expanded).sort();

      return calculateMaxVisibleDepth(rowIds, table.options.rowIdSeparator);
    };

    table.getExpandedDepth = () => {
      let maxDepth = 0;

      const rowIds =
        table.getState().expanded === true
          ? table
              .getRowModel()
              .flatRows.filter(
                (row) => !isRowDisabled(disabledSubRowsCache, row),
              )
              .map((row) => row.id)
          : Object.keys(table.getState().expanded);

      rowIds.forEach((id) => {
        const splitId = id.split('.');
        maxDepth = Math.max(maxDepth, splitId.length);
      });

      return maxDepth;
    };

    // TODO: This is currently unused, but will be necessary when we look at collapsing the indentation.
    /**
     * Gets a calculated width based on the indentation level and collapsing model.
     */
    table.getSubRowIndentationWidth = memo(
      () => [table.getSubRowOpenDepth()],
      (maxDepth: number): number => {
        const iconWidth = 20;
        const limitIndentStep = 4;
        const limitedIndent = 2;
        const defaultIndent = 12;

        const limitedIndentSum =
          Math.floor(maxDepth / limitIndentStep) * limitedIndent;
        const defaultIndentSum =
          (maxDepth - Math.floor(maxDepth / limitIndentStep)) * defaultIndent;

        return limitedIndentSum + defaultIndentSum + iconWidth;
      },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getSubRowIndentationWidth',
      ),
    );

    table.getIsAncestorDisabled = memo(
      () => [table.getExpandedRowModel()],
      () =>
        (row: Row<TData> | undefined): boolean => {
          return isRowDisabled(disabledSubRowsCache, row);
        },
      getMemoOptions(
        table.options,
        'debugTable',
        'table.getIsAncestorDisabled',
      ),
    );

    /**
     * Returns true, if the table has at least one row with at least one sub-row, false otherwise.
     */
    table.getHasSubRows = () =>
      table.getCanSomeRowsExpand() || table.getIsAllRowsExpanded();

    table._getRowLineConfig = memo(
      () => [table.getRowOrderRowModel().rowsById, table.getState().expanded],
      (rowsById) => (rowId: string) => {
        const lineConfig: Array<'Line' | 'TShape' | 'LShape' | 'Empty'> = [];

        const row = rowsById[rowId] as unknown as Row<TData>;

        const isLastExpandedRowOnLevel = row._getIsLastInExpandedOnLevel();

        for (let i = 1; i <= row.depth; i += 1) {
          if (i === row.depth) {
            if (isLastExpandedRowOnLevel) {
              lineConfig.push('LShape');
              continue;
            } else {
              lineConfig.push('TShape');
              continue;
            }
          }
          lineConfig.push('Line');
        }

        // Fill the empty gaps and inherit the ones from the parent as well.
        if (row.parentId) {
          const parentShapes = rowLineConfig.get(row.parentId) ?? [];
          if (parentShapes.at(-1) === 'LShape') {
            lineConfig[lineConfig.length - 2] = 'Empty';
          }
          for (const [index, shape] of parentShapes.entries()) {
            if (shape === 'Empty') {
              lineConfig[index] = 'Empty';
            }
          }
        }

        // Populate the cache with the stored items.
        rowLineConfig.set(rowId, lineConfig);
        return lineConfig;
      },
      getMemoOptions(table.options, 'debugTable', 'table._getRowLineConfig'),
    );
  },

  createRow<TData extends DataTableV2RowData>(
    row: Row<TData>,
    table: Table<TData>,
  ) {
    row.toggleExpanded = (expanded) => {
      table.setExpanded((prev) => {
        // true, if the current row was expanded before
        const exists = prev === true ? true : !!prev?.[row.id];

        let prevExpandedState: ExpandedStateList = {};
        if (prev === true) {
          // ALL rows were expanded before
          table.getCoreRowModel().flatRows.forEach((row) => {
            if (
              !row.getCanExpandDisabled() ||
              !table.getIsAncestorDisabled()(row)
            ) {
              prevExpandedState[row.id] = true;
            }
          });
        } else {
          prevExpandedState = prev;
        }

        const shouldExpand = expanded ?? !exists;
        if (!exists && shouldExpand) {
          // row should expand and was not expanded before → add to new expand state
          return {
            ...prevExpandedState,
            [row.id]: true,
          };
        }

        if (exists && !shouldExpand) {
          // row was expanded before and should be collapsed
          for (const key in prevExpandedState) {
            // also collapse all its sub-rows
            if (key.startsWith(row.id)) {
              delete prevExpandedState[key];
            }
          }
          // remove row itself from expand state
          const { [row.id]: _, ...rest } = prevExpandedState;
          return rest;
        }

        // row should not expand and also was not expanded before
        // do nothing and return old state
        return prev;
      });
    };

    row._getIsLastInExpandedOnLevel = () => {
      const orderedRows = table.getRowOrderRowModel().flatRows;
      const parentRow = row.getParentRow() ?? [];
      const orderedParentRow = orderedRows.find((sortedRow) => {
        return isMatch(sortedRow, parentRow);
      });
      // If we have a parentRow, we can check if we are the last in the subrow nodes?
      if (orderedParentRow) {
        const parentSubRows = orderedParentRow.subRows;

        if (
          parentSubRows &&
          isEqual(parentSubRows.at(-1)?.original, row.original)
        ) {
          return true;
        }
      }
      return false;
    };

    row._getLastExpandedSubRow = () => {
      const orderedRows = table.getRowOrderRowModel().flatRows;
      const orderedRow = orderedRows.find((sortedRow) => {
        return isMatch(sortedRow, row);
      });

      return (
        orderedRow
          ?.getLeafRows()
          // only consider sub-rows that are actually expanded
          .filter((subRow) => subRow.getParentRow()?.getIsExpanded())
          .at(-1)
      );
    };

    row.getIsExpanded = () => {
      const expanded = table.getState().expanded;
      const isAncestorDisabled = table.getIsAncestorDisabled()(row);

      return !!(
        !row.getCanExpandDisabled() &&
        !isAncestorDisabled &&
        (expanded === true || expanded[row.id] === true)
      );
    };

    /** Determines whether or not the given subRow expander is disabled. */
    row.getCanExpandDisabled = () => {
      if (table.options.getRowExpandDisabled) {
        return table.options.getRowExpandDisabled(row.original);
      }
      return false;
    };

    row.getSubRowDepth = () =>
      row.id.split(table.options.rowIdSeparator || '.').length - 1;

    row.getExpandedLeafRows = memo(
      () => [row.getLeafRows()],
      (leafRows) =>
        leafRows.filter((leafRow) => leafRow.getParentRow()?.getIsExpanded()),
      {
        key: isDev() && 'getExpandedLeafRowsWithoutBuiltin',
      },
    );
  },
};

/**
 * @internal
 */
export function SubRowsHeaderWidthCorrection() {
  const table = useDataTableV2();

  // TODO This calculation is almost shared with the one from the width calculation of the rowExpander. This should be moved into the calculation `table.getSubRowIndentationWidth` when we start work on the collapsing expansion width.
  const indentGrid = 16;
  const iconSize = 20;
  const width = indentGrid * table.getSubRowOpenDepth();

  let density = Spacings.Size8;
  const configuredDensity = table.getVisualVariantOptions().rowDensity;
  if (configuredDensity === 'condensed') {
    density = Spacings.Size2;
  } else if (configuredDensity === 'comfortable') {
    density = Spacings.Size12;
  }
  return (
    <div
      style={{
        ...assignInlineVars({ [`${subRowIconSize}`]: `${iconSize}px` }),
        // Calculate the width based on icon button size (icon size + 2 * Size2 padding) and the padding to the right Size8
        width: `calc(${width + iconSize}px + ${density} + ${Spacings.Size4})`,
      }}
    />
  );
}

/**
 * Configuration hook for subrows of the DataTableV2.
 * @internal
 */
export function useSubRows<TData extends DataTableV2RowData>(
  props: DataTableV2SubRowsProps<TData>,
  options: TableOptions<TData>,
) {
  const { subRows } = props;

  const [openSubRows, setOpenSubRows] = useControllableState({
    value: props.openSubRows,
    defaultValue: props.defaultOpenSubRows,
    onChange: (value) => {
      if (value !== true) {
        props.onOpenSubRowsChange?.(value);
      }
    },
  });

  if (subRows === undefined || subRows === false) {
    options.enableExpanding = false;
    return;
  }

  options.enableExpanding = true;

  // If `subRows` is only set as a boolean prop, we can default to the standard
  // subrow accessor.
  if (typeof subRows === 'boolean') {
    options.getSubRows = defaultSubRowsAccessor;
  } else {
    // If a config object is given, we can handle defaulting here as well
    const { accessor, subRowColumnId: subrowColumnId, disableSubRow } = subRows;

    // Default the accessor if subRows are enabled, but no accessor is given.
    if (accessor === undefined) {
      options.getSubRows = defaultSubRowsAccessor;
    }
    // Allow a simple string accessor for the subRows.
    else if (typeof accessor === 'string') {
      if (accessor.includes('.') || accessor.includes('[')) {
        const pathAccessFunction = makeAccessorFunction(accessor);
        options.getSubRows = (originalRow) =>
          pathAccessFunction(originalRow) as TData[];
      } else {
        options.getSubRows = (originalRow) =>
          originalRow[accessor as keyof TData] as TData[];
      }
    }
    // If a function is given, we can use this one to get access to the subrows.
    else if (typeof accessor === 'function') {
      options.getSubRows = (originalRow) => accessor(originalRow);
    }

    // If a custom columnId is given for the injection,
    // we map that into the options to be respected in `getSubRowIndicatorColumnId`
    if (subrowColumnId) {
      options.subRowColumnId = subrowColumnId;
    }

    // If a custom disable configuration is given, we can map that to the
    // getRowCanExpand option and shimming the internal tanstack `Row` object.
    if (disableSubRow) {
      options.getRowExpandDisabled = disableSubRow;
    }
  }

  // Pass the state through to the table.
  options.state = {
    ...options.state,
    expanded: openSubRows ?? {},
  };
  options.onExpandedChange = setOpenSubRows;
}
